'use client';

import IconButton from '@/components/base/button/icon-button';
import { MinusCircle, PlusCircle } from '@phosphor-icons/react/dist/ssr';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, useEffect, useState } from 'react';

interface Props {
  value: number;
  onChange: (newValue: number) => void;
  disabled?: boolean;
  isLoading?: boolean;
  triggerReset: boolean;
  testId?: string;
}

const InputStepper = ({
  value,
  onChange,
  disabled,
  isLoading,
  triggerReset,
  testId,
}: Props) => {
  const t = useTranslations('common');
  const [inputValue, setInputValue] = useState(value);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value || '0';
    if (newValue.length > 3) return;

    setInputValue(parseInt(newValue, 0));
  };

  const onInputBlur = () => {
    onChange(inputValue);
  };

  useEffect(() => {
    setInputValue(value);
  }, [value, triggerReset]);

  return (
    <div
      id="number-input-stepper"
      data-testid={testId}
      className={clsx(
        'flex h-clickable-minimum items-center rounded-s hover:bg-main hover:shadow-[0px_0px_0px_3px_#F7F6F6] hover:outline hover:outline-strong has-[:focus]:outline has-[:focus]:outline-strong',
      )}
    >
      <IconButton
        Icon={MinusCircle}
        size="small"
        aria-label={t('minus')}
        iconVariant="regular"
        className="hover:text-brandMain hover:outline-none"
        onClick={() => onChange(value - 1)}
        isLoading={isLoading}
        disabled={disabled}
        testId="decrease_quantity"
      />
      <input
        type="number"
        min={0}
        max={999}
        data-testid="product_quantity_input"
        step="1"
        aria-live="polite"
        aria-atomic
        maxLength={3}
        className={clsx(
          'h-full w-clickable-minimum cursor-pointer text-center tabular-nums focus:z-10 focus:shadow-[0px_0px_0px_1px_#FFF,0px_0px_0px_3px_#2E2B29] focus:outline-none',
        )}
        value={inputValue}
        onChange={onInputChange}
        onBlur={onInputBlur}
      />
      <IconButton
        Icon={PlusCircle}
        aria-label={t('plus')}
        size="small"
        iconVariant="regular"
        className="hover:text-brandMain hover:outline-none"
        onClick={() => onChange(value + 1)}
        isLoading={isLoading}
        disabled={disabled}
        testId="increase_quantity"
      />
    </div>
  );
};

export default InputStepper;
